import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DEMO_ACCOUNT, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("b2b") ? "register" : "login");
  const [isB2b, setIsB2b] = useState(!!params.get("b2b"));
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", company_name: "", reg_number: "", vat_number: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user, login, register, demoLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/konts"); }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({
          email: form.email, password: form.password, name: form.name, phone: form.phone || null,
          is_b2b: isB2b, company_name: isB2b ? form.company_name : null,
          reg_number: isB2b ? form.reg_number : null, vat_number: isB2b ? form.vat_number : null,
        });
        if (isB2b) toast.success("B2B pieteikums saņemts! Individuālās atlaides tiks aktivizētas pēc apstiprināšanas.");
      }
      navigate("/konts");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <div className="rounded-2xl overflow-hidden border border-slate-300">
        <div className="grid grid-cols-2">
          <button data-testid="tab-login" onClick={() => { setMode("login"); setError(""); }}
            className={`py-3.5 text-sm font-semibold tracking-wide btn-industrial ${mode === "login" ? "bg-[#1D1D1F] text-white" : "hover:bg-[#F4F5F7]"}`}>Pieteikties</button>
          <button data-testid="tab-register" onClick={() => { setMode("register"); setError(""); }}
            className={`py-3.5 text-sm font-semibold tracking-wide btn-industrial ${mode === "register" ? "bg-[#1D1D1F] text-white" : "hover:bg-[#F4F5F7]"}`}>Reģistrēties</button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="border border-blue-200 bg-blue-50 p-3 text-xs leading-relaxed text-slate-600" data-testid="auth-demo-notice">
            <p className="font-bold text-[#1D1D1F]">Demo versija</p>
            <p>Dati paliek tikai šajā pārlūkā. Neizmantojiet īstu paroli.</p>
            <p className="mt-1 font-mono">{DEMO_ACCOUNT.email} · {DEMO_ACCOUNT.password}</p>
          </div>
          {mode === "register" && (
            <>
              <input data-testid="auth-name" placeholder="Vārds, uzvārds *" value={form.name} onChange={set("name")} required className={inputCls} />
              <input data-testid="auth-phone" placeholder="Tālrunis" value={form.phone} onChange={set("phone")} className={inputCls} />
            </>
          )}
          <input data-testid="auth-email" type="email" placeholder="E-pasts *" value={form.email} onChange={set("email")} required className={inputCls} />
          <input data-testid="auth-password" type="password" placeholder="Parole * (min. 6 simboli)" value={form.password} onChange={set("password")} required minLength={6} className={inputCls} />

          {mode === "register" && (
            <div className="border border-border p-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="auth-b2b-checkbox" type="checkbox" checked={isB2b} onChange={(e) => setIsB2b(e.target.checked)} className="h-4 w-4 accent-[#2E86D1]" />
                <span className="text-sm font-bold">Reģistrēties kā B2B klients</span>
              </label>
              <p className="mt-1 text-xs text-slate-500">Juridiskām personām — individuālas apjoma atlaides un apmaksa ar rēķinu.</p>
              {isB2b && (
                <div className="mt-3 space-y-3">
                  <input data-testid="auth-company" placeholder="Uzņēmuma nosaukums *" value={form.company_name} onChange={set("company_name")} required className={inputCls} />
                  <input data-testid="auth-regnr" placeholder="Reģistrācijas numurs *" value={form.reg_number} onChange={set("reg_number")} required className={inputCls} />
                  <input data-testid="auth-vatnr" placeholder="PVN numurs" value={form.vat_number} onChange={set("vat_number")} className={inputCls} />
                </div>
              )}
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-sm font-semibold text-[#2E86D1]">{error}</p>}

          <button data-testid="auth-submit-btn" type="submit" disabled={submitting}
            className="btn-industrial w-full bg-[#2E86D1] py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2] disabled:opacity-50">
            {submitting ? "Lūdzu, uzgaidiet..." : mode === "login" ? "Pieteikties" : "Izveidot kontu"}
          </button>

          <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-border" />VAI<span className="h-px flex-1 bg-border" /></div>

          <button data-testid="demo-login-btn" type="button" onClick={async () => { await demoLogin(); navigate("/konts"); }}
            className="btn-industrial w-full flex items-center justify-center gap-2 border border-slate-300 py-3.5 text-sm font-semibold tracking-wide hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
            <UserCircle size={18} weight="bold" /> Atvērt B2B demo kontu
          </button>
        </form>
      </div>
    </div>
  );
}
